/**
 * Annotation API client — Phase 2 (Feb 2026).
 *
 * Thin wrappers around the /api/annotations endpoints. All calls use
 * safeFetch + getBackendBaseUrl() per repo convention. Auth is taken
 * from the Supabase session passed in; we don't reach into context
 * here so callers can rely on stable React hooks.
 */
import { safeFetch } from '@/lib/safeFetch';
import { getBackendBaseUrl } from '@/lib/backendUrl';

const API_URL = getBackendBaseUrl();

const authHeaders = (accessToken) => ({
  'Content-Type': 'application/json',
  ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
});

export const createAnnotation = async ({ accessToken, payload }) => {
  const res = await safeFetch(`${API_URL}/api/annotations`, {
    method: 'POST',
    headers: authHeaders(accessToken),
    body: JSON.stringify(payload),
  });
  if (!res.ok) throw new Error(`createAnnotation failed: ${res.status}`);
  return res.json();
};

export const listAnnotations = async ({ accessToken, documentId, status }) => {
  const qs = new URLSearchParams({ document_id: documentId });
  if (status) qs.set('status', status);
  const res = await safeFetch(
    `${API_URL}/api/annotations?${qs.toString()}`,
    { headers: authHeaders(accessToken) }
  );
  if (!res.ok) throw new Error(`listAnnotations failed: ${res.status}`);
  return res.json();
};

export const replyToAnnotation = async ({ accessToken, id, body }) => {
  const res = await safeFetch(
    `${API_URL}/api/annotations/${id}/reply`,
    {
      method: 'POST',
      headers: authHeaders(accessToken),
      body: JSON.stringify({ body }),
    }
  );
  if (!res.ok) throw new Error(`replyToAnnotation failed: ${res.status}`);
  return res.json();
};

export const resolveAnnotation = async ({ accessToken, id }) => {
  const res = await safeFetch(
    `${API_URL}/api/annotations/${id}/resolve`,
    { method: 'PATCH', headers: authHeaders(accessToken) }
  );
  if (!res.ok) throw new Error(`resolveAnnotation failed: ${res.status}`);
  return res.json();
};

export const fetchQueue = async ({ accessToken }) => {
  const res = await safeFetch(
    `${API_URL}/api/annotations/queue`,
    { headers: authHeaders(accessToken) }
  );
  if (!res.ok) throw new Error(`fetchQueue failed: ${res.status}`);
  return res.json();
};
